import React, {createContext, useCallback, useContext, useMemo, useReducer} from "react";
import {normalizeEditableRecord, toStoredEditableText} from "../libs/editableText";
import {isEmptyObj} from "../libs/utils";
import {
  INITIAL_GDN_TAGS, INITIAL_GDN_TAG_ORDER,
  assertGdnTagId,
  assertGdnTagRecord,
  nextGdnTagId,
  normalizeGdnTagOrder,
} from "../libs/gdnTags";

export {INITIAL_GDN_TAGS, INITIAL_GDN_TAG_ORDER} from "../libs/gdnTags";

// Single source of truth for the editable-text slice of a scene.
//
// Adding a new editable field is a one-place change here:
//   - text-record (Record<id, value>)  →  add to INITIAL_STATE + TEXT_RECORD_FIELDS
//   - anything else                    →  add to INITIAL_STATE + a reducer case
//
// The shape used to live in five hand-maintained lists (state hooks, reset,
// loadScene, App.buildScene's body, App.buildScene's dep array). Adding a
// field meant editing all five; missing one silently lost data on save/load.
// SCENE_FIELDS below is the authoritative list — buildScene iterates it and
// loadScene consumes it, so they cannot drift.
export const INITIAL_EDITS_STATE = Object.freeze({
  names: {},
  names2: {},
  names3: {},
  names4: {},
  names5: {},
  names6: {},
  titles: {},
  subtitles: {},
  subtitles2: {},
  subtitles3: {},
  positions: {},
  positions2: {},
  dates: {},
  times: {},
  badges: {},
  productNames: {},
  fontVariation: 1,
  features: {},
  eaps: {},
  tags: {"tag-0": "Tag 1", "tag-1": "Tag 2"},
  gdnTags: INITIAL_GDN_TAGS,
  gdnTagOrder: INITIAL_GDN_TAG_ORDER,
  links: {},
  links2: {},
  links3: {},
  // dataRows / dataRowIndex are transient: loaded from a CSV via the
  // DataSelector, used only during print batch export. They get reset on
  // navigation but are NOT serialized into a saved scene.
  dataRows: [],
  dataRowIndex: 0,
  versions: {},
});

// Fields stored as Record<id, value> editable-text maps. Treated identically
// in load / edit / cleanupLocalChanges paths.
export const TEXT_RECORD_FIELDS = Object.freeze([
  "names",
  "names2",
  "names3",
  "names4",
  "names5",
  "names6",
  "titles",
  "subtitles",
  "subtitles2",
  "subtitles3",
  "positions",
  "positions2",
  "dates",
  "times",
  "badges",
  "productNames",
  "features",
  "eaps",
  "links",
  "links2",
  "links3",
  "versions",
  "gdnTags",
]);

// Fields that participate in scene save/load roundtrips. dataRows and
// dataRowIndex are intentionally excluded (transient, see INITIAL_EDITS_STATE).
export const SCENE_FIELDS = Object.freeze([
  ...TEXT_RECORD_FIELDS,
  "gdnTagOrder",
  "fontVariation",
  "tags",
]);

const TEXT_RECORD_SET = new Set(TEXT_RECORD_FIELDS);

/**
 * True when a person did not change the text field: it is empty, or it
 * still holds `prev`, the placeholder of the route before.
 */
export function isUntouchedTextRecord(record, prev) {
  return isEmptyObj(record) || (prev !== undefined && record.main === prev);
}

export function editsReducer(state, action) {
  switch (action.type) {
    case "RESET":
      return INITIAL_EDITS_STATE;

    case "LOAD_SCENE": {
      const {data} = action;
      const next = {...state};
      for (const field of TEXT_RECORD_FIELDS) {
        if (data[field] !== undefined) {
          if (field === "gdnTags") assertGdnTagRecord(data[field]);
          next[field] = normalizeEditableRecord(data[field]);
        }
      }
      if (data.gdnTags !== undefined || data.gdnTagOrder !== undefined) {
        next.gdnTagOrder = normalizeGdnTagOrder(data.gdnTagOrder, next.gdnTags);
      }
      // productNames historically resets to {} when missing from scene
      // data — every other text-record field stays at its previous value.
      // Preserved to avoid surprising scenes that rely on the old behavior.
      if (data.productNames === undefined) {
        next.productNames = {};
      }
      if (data.fontVariation !== undefined) {
        next.fontVariation = data.fontVariation;
      }
      // typeof [] === "object" and [] is truthy, so guard arrays explicitly —
      // a malformed payload with tags: [] would otherwise be stored as the
      // tags value, breaking every downstream consumer that iterates
      // Object.keys(tags) expecting a {[id]: string} record.
      if (data.tags != null && typeof data.tags === "object" && !Array.isArray(data.tags)) {
        next.tags = data.tags;
      }
      return next;
    }

    // A default scene: load each text field it carries and keep the others.
    // Unlike LOAD_SCENE, an absent productNames stays as it is.
    case "LOAD_TEXT_FIELDS": {
      const {data} = action;
      const next = {...state};
      for (const field of TEXT_RECORD_FIELDS) {
        if (data[field] == null) continue;
        if (field === "gdnTags") assertGdnTagRecord(data[field]);
        next[field] = normalizeEditableRecord(data[field]);
      }
      if (data.gdnTags != null || data.gdnTagOrder != null) {
        next.gdnTagOrder = normalizeGdnTagOrder(data.gdnTagOrder ?? undefined, next.gdnTags);
      }
      if (data.tags != null && typeof data.tags === "object" && !Array.isArray(data.tags)) {
        next.tags = data.tags;
      }
      return next;
    }

    // The placeholders of the route (hooks/useEnsureDefaultEdits). Each goes
    // only into a field that a person did not change. The check reads this
    // state, not the state of the render that made the seeds: a default
    // scene can load its texts in the same commit, after that render.
    case "SEED_TEXT_RECORDS": {
      let next = state;
      for (const {field, value, prev} of action.seeds) {
        if (!TEXT_RECORD_SET.has(field)) {
          throw new Error(`SEED_TEXT_RECORDS: unknown field "${field}"`);
        }
        const current = next[field];
        if (!isUntouchedTextRecord(current, prev) || current.main === value) continue;
        if (next === state) next = {...state};
        next[field] = {...current, main: toStoredEditableText(value)};
      }
      return next;
    }

    // A new channel/product/type/set: clear every text field before its
    // default scene loads.
    case "RESET_TEXT_FIELDS": {
      const next = {...state};
      for (const field of TEXT_RECORD_FIELDS) next[field] = INITIAL_EDITS_STATE[field];
      next.gdnTagOrder = INITIAL_EDITS_STATE.gdnTagOrder;
      next.tags = INITIAL_EDITS_STATE.tags;
      return next;
    }

    case "EDIT_TEXT_RECORD": {
      const {field, value, id, isLocked} = action;
      if (!TEXT_RECORD_SET.has(field)) {
        throw new Error(`EDIT_TEXT_RECORD: unknown field "${field}"`);
      }
      const currentId = isLocked ? id : "main";
      return {
        ...state,
        [field]: {
          ...state[field],
          [currentId]: toStoredEditableText(value),
        },
      };
    }

    case "LOAD_TEXT_RECORD": {
      if (!TEXT_RECORD_SET.has(action.field)) {
        throw new Error(`LOAD_TEXT_RECORD: unknown field "${action.field}"`);
      }
      const value = normalizeEditableRecord(action.value);
      if (action.field === "gdnTags") {
        assertGdnTagRecord(value);
        return {
          ...state,
          gdnTags: value,
          gdnTagOrder: normalizeGdnTagOrder(undefined, value),
        };
      }
      return {
        ...state,
        [action.field]: value,
      };
    }

    case "LOAD_GDN_TAG_ORDER":
      return {
        ...state,
        gdnTagOrder: normalizeGdnTagOrder(action.value, state.gdnTags),
      };

    case "ADD_GDN_TAG": {
      const id = nextGdnTagId(state.gdnTags);
      return {
        ...state,
        gdnTags: {...state.gdnTags, [id]: toStoredEditableText(action.value)},
        gdnTagOrder: [...state.gdnTagOrder, id],
      };
    }

    case "REMOVE_GDN_TAG": {
      const {id} = action;
      assertGdnTagId(id);
      if (!Object.prototype.hasOwnProperty.call(state.gdnTags, id)) {
        throw new Error(`Cannot remove missing GDN tag: ${id}`);
      }
      const gdnTags = {...state.gdnTags};
      delete gdnTags[id];
      return {
        ...state,
        gdnTags,
        gdnTagOrder: state.gdnTagOrder.filter((tagId) => tagId !== id),
      };
    }

    case "SET_PRODUCT_NAMES":
      return {
        ...state,
        productNames: normalizeEditableRecord(action.value) || {},
      };

    case "SET_FONT_VARIATION":
      return {...state, fontVariation: action.value};

    case "SET_TAGS": {
      const next = typeof action.value === "function" ? action.value(state.tags) : action.value;
      return {...state, tags: next};
    }

    case "SET_DATA_ROWS":
      return {...state, dataRows: Array.isArray(action.value) ? action.value : []};

    case "SET_DATA_ROW_INDEX":
      return {...state, dataRowIndex: action.value};

    case "CLEANUP_LOCAL_CHANGES": {
      const {id} = action;
      let next = state;
      for (const field of TEXT_RECORD_FIELDS) {
        const cur = state[field];
        if (cur && Object.prototype.hasOwnProperty.call(cur, id)) {
          if (next === state) next = {...state};
          const copy = {...cur};
          delete copy[id];
          next[field] = copy;
        }
      }
      return next;
    }

    default:
      return state;
  }
}

const NOOP = () => {};

export const Edits = createContext({
  ...INITIAL_EDITS_STATE,
  reset: NOOP,
  loadScene: NOOP,
  loadTextFields: NOOP,
  resetTextFields: NOOP,
  seedTextRecords: NOOP,
  cleanupLocalChanges: NOOP,
  setTags: NOOP,
  setDataRowIndex: NOOP,
  loadDataRows: NOOP,
  clearDataRows: NOOP,
  editFontVariation: NOOP,
  editProductName: NOOP,
  editProductNames: NOOP,
  editTitles: NOOP,
  editSubtitles: NOOP,
  editSubtitles2: NOOP,
  editSubtitles3: NOOP,
  editNames: NOOP,
  editNames2: NOOP,
  editPositions: NOOP,
  editDates: NOOP,
  editTimes: NOOP,
  editBadges: NOOP,
  editEaps: NOOP,
  editFeatures: NOOP,
  editGdnTags: NOOP,
  addGdnTag: NOOP,
  removeGdnTag: NOOP,
  editVersions: NOOP,
  editLinks: NOOP,
  editLinks2: NOOP,
  editLinks3: NOOP,
  loadTitles: NOOP,
  loadSubtitles: NOOP,
  loadSubtitles2: NOOP,
  loadSubtitles3: NOOP,
  loadNames: NOOP,
  loadNames2: NOOP,
  loadPositions: NOOP,
  loadDates: NOOP,
  loadTimes: NOOP,
  loadBadges: NOOP,
  loadEaps: NOOP,
  loadFeatures: NOOP,
  loadGdnTags: NOOP,
  loadGdnTagOrder: NOOP,
  loadVersions: NOOP,
  loadLinks: NOOP,
  loadLinks2: NOOP,
  loadLinks3: NOOP,
  loadProductNames: NOOP,
});

export const EditsContextProvider = ({children}) => {
  const [state, dispatch] = useReducer(editsReducer, INITIAL_EDITS_STATE);

  // dispatch identity is stable across renders, so every wrapper memoized
  // with [] deps below is also stable. Consumers like useEnsureDefaultEdits
  // rely on this — their useEffect deps include these callbacks and would
  // re-fire every render if identity changed.
  const reset = useCallback(() => dispatch({type: "RESET"}), []);
  const loadScene = useCallback((data) => dispatch({type: "LOAD_SCENE", data}), []);
  const loadTextFields = useCallback((data) => dispatch({type: "LOAD_TEXT_FIELDS", data}), []);
  const resetTextFields = useCallback(() => dispatch({type: "RESET_TEXT_FIELDS"}), []);
  const seedTextRecords = useCallback(
    (seeds) => dispatch({type: "SEED_TEXT_RECORDS", seeds}),
    []
  );
  const cleanupLocalChanges = useCallback(
    (id) => dispatch({type: "CLEANUP_LOCAL_CHANGES", id}),
    []
  );
  const setTags = useCallback((value) => dispatch({type: "SET_TAGS", value}), []);
  const setDataRowIndex = useCallback((value) => dispatch({type: "SET_DATA_ROW_INDEX", value}), []);
  const loadDataRows = useCallback((rows) => dispatch({type: "SET_DATA_ROWS", value: rows}), []);
  const clearDataRows = useCallback(() => dispatch({type: "SET_DATA_ROWS", value: []}), []);
  const editFontVariation = useCallback(
    (value) => dispatch({type: "SET_FONT_VARIATION", value}),
    []
  );
  const editProductName = useCallback(
    (value, id, isLocked = false) =>
      dispatch({type: "EDIT_TEXT_RECORD", field: "productNames", value, id, isLocked}),
    []
  );
  const editProductNames = useCallback((value) => dispatch({type: "SET_PRODUCT_NAMES", value}), []);
  const addGdnTag = useCallback(
    (value = "tag") => dispatch({type: "ADD_GDN_TAG", value}),
    []
  );
  const removeGdnTag = useCallback((id) => dispatch({type: "REMOVE_GDN_TAG", id}), []);
  const loadGdnTagOrder = useCallback(
    (value) => dispatch({type: "LOAD_GDN_TAG_ORDER", value}),
    []
  );

  // Per-field text-record helpers. The factory removes ~200 lines of
  // near-duplicated setX boilerplate while keeping each public name
  // grep-able from consumer call sites.
  const textRecordWrappers = useMemo(() => {
    const editors = {};
    const loaders = {};
    for (const field of TEXT_RECORD_FIELDS) {
      editors[field] = (value, id, isLocked = false) =>
        dispatch({type: "EDIT_TEXT_RECORD", field, value, id, isLocked});
      loaders[field] = (value) => dispatch({type: "LOAD_TEXT_RECORD", field, value});
    }
    return {editors, loaders};
  }, []);

  const value = useMemo(() => {
    const {editors: e, loaders: l} = textRecordWrappers;
    return {
      ...state,
      reset,
      loadScene,
      loadTextFields,
      resetTextFields,
      seedTextRecords,
      cleanupLocalChanges,
      setTags,
      setDataRowIndex,
      loadDataRows,
      clearDataRows,
      editFontVariation,
      editProductName,
      editProductNames,
      addGdnTag,
      removeGdnTag,
      loadGdnTagOrder,
      editTitles: e.titles,
      editSubtitles: e.subtitles,
      editSubtitles2: e.subtitles2,
      editSubtitles3: e.subtitles3,
      editNames: e.names,
      editNames2: e.names2,
      editNames3: e.names3,
      editNames4: e.names4,
      editNames5: e.names5,
      editNames6: e.names6,
      editPositions: e.positions,
      editPositions2: e.positions2,
      editDates: e.dates,
      editTimes: e.times,
      editBadges: e.badges,
      editEaps: e.eaps,
      editFeatures: e.features,
      editGdnTags: e.gdnTags,
      editVersions: e.versions,
      editLinks: e.links,
      editLinks2: e.links2,
      editLinks3: e.links3,
      loadTitles: l.titles,
      loadSubtitles: l.subtitles,
      loadSubtitles2: l.subtitles2,
      loadSubtitles3: l.subtitles3,
      loadNames: l.names,
      loadNames2: l.names2,
      loadNames3: l.names3,
      loadNames4: l.names4,
      loadNames5: l.names5,
      loadNames6: l.names6,
      loadPositions: l.positions,
      loadPositions2: l.positions2,
      loadDates: l.dates,
      loadTimes: l.times,
      loadBadges: l.badges,
      loadEaps: l.eaps,
      loadFeatures: l.features,
      loadGdnTags: l.gdnTags,
      loadVersions: l.versions,
      loadLinks: l.links,
      loadLinks2: l.links2,
      loadLinks3: l.links3,
      loadProductNames: l.productNames,
    };
  }, [
    state,
    textRecordWrappers,
    reset,
    loadScene,
    loadTextFields,
    resetTextFields,
    seedTextRecords,
    cleanupLocalChanges,
    setTags,
    setDataRowIndex,
    loadDataRows,
    clearDataRows,
    editFontVariation,
    editProductName,
    editProductNames,
    addGdnTag,
    removeGdnTag,
    loadGdnTagOrder,
  ]);

  return <Edits.Provider value={value}>{children}</Edits.Provider>;
};

export function useEdits() {
  const context = useContext(Edits);
  if (context === undefined) {
    throw new Error("Edits.Context must be used within the Edits.Provider");
  }
  return context;
}
